/**
 * Bounded "first time I've seen this key" gate for de-duplicating log lines
 * per isolate (COM-595).
 *
 * A Worker isolate lives for hours and serves every route, so a `console.warn`
 * that fires once per render of a fixed input (an over-long SEO description on
 * a PLP, a dangling master id on a hot PDP) repeats tens of thousands of times a
 * day and buries the signal. Callers key on the stable input and log only when
 * the gate opens.
 *
 * Memory is bounded: once `maxKeys` distinct keys are held, the OLDEST key is
 * evicted (Set iteration is insertion-ordered), so a long tail of unique keys
 * (one per product id, say) can never grow the set past the cap. Eviction means
 * a very old key may warn again later — acceptable for a log-volume bound, and
 * strictly better than the unbounded alternative.
 *
 * Module-scope by design: the caller owns exactly one gate per log signature.
 *
 *   const warnDangling = createOnceGate();
 *   if (warnDangling(`${masterId}|${productId}`)) console.warn(...);
 */
export const DEFAULT_ONCE_GATE_MAX_KEYS = 500;

export type OnceGate = ((key: string) => boolean) & {
  /** Number of keys currently held (tests / diagnostics). */
  readonly size: number;
};

export const createOnceGate = (
  maxKeys: number = DEFAULT_ONCE_GATE_MAX_KEYS,
): OnceGate => {
  const cap =
    Number.isFinite(maxKeys) && maxKeys >= 1 ? Math.floor(maxKeys) : 1;
  const seen = new Set<string>();
  const gate = ((key: string): boolean => {
    if (seen.has(key)) return false;
    if (seen.size >= cap) {
      const oldest = seen.values().next().value;
      if (oldest !== undefined) seen.delete(oldest);
    }
    seen.add(key);
    return true;
  }) as OnceGate;
  Object.defineProperty(gate, "size", { get: () => seen.size });
  return gate;
};
